import { useMutation } from '@apollo/client/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { z } from 'zod'
import { BrandLogo } from '~/components/BrandLogo'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormMessage } from '~/components/ui/form'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { GQL_SEND_RESET_PASSWORD } from '~/gql/auth/auth'

function ResetPassword() {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const resetSchema = z.object({
    email: z.string().email({ message: t('input.email.message') }),
  })

  type ResetFormValues = z.infer<typeof resetSchema>

  const form = useForm<ResetFormValues>({
    resolver: zodResolver(resetSchema),
    defaultValues: {
      email: '',
    },
  })

  const [sendResetPassword, { loading }] = useMutation(GQL_SEND_RESET_PASSWORD)

  const onSubmit = async (values: ResetFormValues) => {
    try {
      const { data } = await sendResetPassword({ variables: { email: values.email } })
      if (data && data.askForResetPassword) {
        toast.success(t('reset.password.success'))
        setTimeout(() => {
          navigate('/')
        }, 2000)
      } else {
        toast.error(t('bad.reset.message'))
      }
    } catch {
      toast.error(t('bad.server-login.message'))
    }
  }

  return (
    <div className="flex min-h-dvh w-screen flex-col items-center justify-center p-5">
      <Card className="sm:w-md w-full">
        <CardHeader className="gap-4">
          <BrandLogo className="h-9 w-auto" fallbackClassName="text-lg" />
          <div className="flex flex-col gap-1">
            <CardTitle className="font-heading text-2xl tracking-tight">{t('reset.password')}</CardTitle>
            <p className="text-sm text-muted-foreground">{t('reset.password.description')}</p>
          </div>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="w-full mx-auto">
                    <FormControl>
                      <div className="grid gap-2">
                        <Label htmlFor="email">{t('common.email')}</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="m@example.com"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" disabled={loading} className="w-full">
                {loading && <Loader className="mr-2 size-4 animate-spin" />}
                {t('common.submit')}
              </Button>

              <div className="mt-4 text-center text-sm">
                <a
                  onClick={() => navigate('/')}
                  className="underline underline-offset-4 cursor-pointer"
                >
                  {t('login.return-message')}
                </a>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  )
}

export default ResetPassword
